import i18next from 'i18next';
import { parse, format } from 'date-fns';
import moment, { Moment } from 'moment';
import { isNaN } from 'lodash';

export const parseAndFormatDate = (
  dateString: string,
  sourceFormat: string,
  destinationFormat: string,
) => {
  try {
    return format(
      parse(dateString, sourceFormat, new Date()),
      destinationFormat,
    );
  } catch {
    return '';
  }
};

export const momentDateFormat = (
  date?: Moment | string | null,
  destFormat?: string,
) => {
  if (date) {
    if (typeof date !== 'string') {
      return date.format(destFormat);
    }
    return moment(date).format(destFormat);
  }
  return moment();
};

export const formatDate = (date: Date, destinationFormat: string) => {
  return format(date, destinationFormat);
};

export const dateFormat = (date: string) => {
  if (date) {
    const dateObj = new Date(date);
    return dateObj.toLocaleString(i18next.language);
  }
  return '';
};

export const formatDateTime = (dateString: string) => {
  const data = new Date(dateString);

  data.setHours(data.getHours() - 3);

  function pad(num: number) {
    return num.toString().padStart(2, '0');
  }

  const day = pad(data.getDate());
  const month = pad(data.getMonth() + 1);
  const year = data.getFullYear();
  const hour = pad(data.getHours());
  const minutes = pad(data.getMinutes());

  const formatedHouder = `${day}/${month}/${year} ${hour}:${minutes}`;

  return formatedHouder;
};

export const unixTimestampFormat = (date: number) => {
  if (date) {
    const dateObj = new Date(date * 1000);
    return dateObj.toLocaleString(i18next.language);
  }
  return '';
};

// 2011-10-05 -> 05/10/2011
export const getDateFromPartiallyInIsoFormat = (date: any) => {
  // eslint-disable-next-line no-param-reassign
  date = date?.split('-')?.reverse();
  return date?.join('/');
};

// 2011-10-05T14:48:00.000Z -> 20111005
export const getDateInISOFormat = (date: any) => {
  const newDate = date?.split('T')?.[0];
  return newDate?.replaceAll('-', '');
};

// 2011-10-05T14:48:00.000Z -> 05/10/2011
export const extractDateInISOFormat = (date: any) => {
  const dateSplit = date?.split('T')?.[0];
  return getDateFromPartiallyInIsoFormat(dateSplit ?? date);
};

//  20111005 --> 2011-10-05T14:48:00.000Z
export const getDateWithISOFormat = (date: any, startOrEnd?: any) => {
  const newDate = moment(date, 'YYYYMMDD');
  const isEndData = startOrEnd === 'end';
  newDate.set({
    hour: isEndData ? 23 : 0,
    minute: isEndData ? 59 : 0,
    second: isEndData ? 59 : 0,
    millisecond: isEndData ? 999 : 0,
  });
  return newDate?.toISOString();
};

// DD/MM/YYYY --> YYYYMMDD
export const getDateFromBRPattern = (date: any) =>
  date?.split('/').reverse().join('');

// YYYYMMDD --> DD/MM/YYYY
export const getDateToBRPattern = (date: any) =>
  date &&
  getDateWithISOFormat(date).split('T')[0].split('-').reverse().join('/');

export const toISOStringWithOffset = (date: Moment, offset = 0) => {
  const localDate = new Date(String(date));

  localDate.setMinutes(
    localDate.getMinutes() - localDate.getTimezoneOffset() + offset * 60,
  );

  return localDate.toISOString();
};

export const bytesToMbytes = (bytes: number, fixed = 3) => {
  return (bytes / 1048576).toFixed(fixed);
};

export const dateMonthFormat = (str: string, t: any) => {
  const date = str.split('-').map(Number);
  const newDate = new Date(date[2], date[1] - 1, date[0]);
  const month = newDate.getMonth() + 1;
  return `${newDate.getDate()} ${t('invoice.of')} ${t(
    `invoice.months.${month}`,
  ).slice(0, 3)}`;
};

export const formatDateTimeWithOffset = (date: Date, offset = 0) => {
  const localDate = new Date(date);

  localDate.setMinutes(
    localDate.getMinutes() - localDate.getTimezoneOffset() + offset * 60,
  );

  return localDate.toLocaleString(i18next.language);
};

export const formatCpfCnpj = (value: string) => {
  const clean = value ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : '';

  if (/^\d{0,11}$/.test(clean)) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  return clean.replace(
    /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  );
};

export const bytesFormat = (value?: string | number) => {
  if (!value) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

  let newValue = typeof value === 'string' ? parseFloat(value) : value;
  newValue = isNaN(newValue) ? 0 : newValue;

  let unitIndex = 0;

  while (newValue >= 1024 && unitIndex < units.length - 1) {
    newValue /= 1024;
    unitIndex += 1;
  }

  return `${parseFloat(newValue.toFixed(2))} ${units[unitIndex]}`;
};

export const toBytes = (value: number, unit?: 'B' | 'KB' | 'MB' | 'GB') => {
  if (!unit || unit === 'B') return value;

  const units = {
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
  };

  return value * units[unit];
};

export const formatPricePerUnit = (pricePerMB: number) => {
  const priceNumber = Number(pricePerMB);
  if (!priceNumber || isNaN(priceNumber)) return undefined;

  let unit = 'MB';
  let price = priceNumber;

  if (priceNumber < 0.01) {
    price = priceNumber * 1024;
    unit = 'GB';
  } else if (priceNumber >= 1024) {
    price = priceNumber / 1024;
    unit = 'KB';
  }

  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

  return `${formatter.format(price)} / ${unit}`;
};

export const formatPricePerMB = (
  unit: 'KB' | 'MB' | 'GB',
  price?: number | '0',
) => {
  if (!price || price === '0') return undefined;

  switch (unit) {
    case 'KB':
      return price * 1024;
    case 'MB':
      return price;
    case 'GB':
      return price / 1024;
    default:
      return price;
  }
};

// Conversion:: Seconds string > HH:mm:ss
export const hourFormat = (text: string) => {
  if (text) {
    const textn = parseInt(text, 10);

    const hours = Math.floor(textn / 3600);
    const minutes = Math.floor((textn % 3600) / 60);
    const seconds = textn % 60;

    const str = [hours, minutes, seconds]
      .map(element => {
        return String(element).padStart(2, '0');
      })
      .join(':');
    return str;
  }
  return '';
};

export const formatSize = (size: number) => {
  const isMega = size < 1073741824;
  const isGiga = size < 1099511627776;
  const isTera = size < 1125899906842600;

  const sizeToMega = size / 1024 / 1024;

  let newSize = [sizeToMega, 'MB'];

  if (isMega) {
    newSize = [sizeToMega, 'MB'];
  } else if (isGiga) {
    newSize = [sizeToMega / 1024, 'GB'];
  } else if (isTera) {
    newSize = [sizeToMega / 1024 / 1024, 'TB'];
  } else {
    newSize = [sizeToMega / 1024 / 1024 / 1024, 'PB'];
  }

  return `${formatBRNumber(Number(Number(newSize[0]).toFixed(3)))} ${
    newSize[1]
  }`;
};

const brlFormatter = (maximumFractionDigits?: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: maximumFractionDigits ?? 6,
  });

export const formatCurrency = (
  value?: number,
  maximumFractionDigits?: number,
) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }

  return brlFormatter(maximumFractionDigits).format(value);
};

export const parseCurrency = (value: string) => {
  if (!value || value === '') {
    return 0;
  }

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatBRNumber = (value: number | string) =>
  value?.toLocaleString('pt-BR');

export const consumptionPercentage: (
  consumption: string,
  total: string,
) => [number, string] = (consumption: string, total: string) => {
  let percent;
  try {
    percent = +consumption / (+total * 1024 * 1024);
    if (!percent) {
      return [0, 'gray'];
    }
  } catch (err) {
    return [0, 'gray'];
  }

  let color = 'green';
  if (percent >= 0.8 && percent < 1) {
    color = 'orange';
  } else if (percent > 1) {
    color = 'red';
  }

  return [+percent.toFixed(2), color];
};

// i Thu Feb 24 2022 16:58:22 GMT-0300 (Brasilia Standard Time)
// --->
// { firstDay: "20210201", lastDay: "20210228" }
export const handleFirstAndLastDayOfMonth = (date: any) => {
  const months = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
  ];

  const month = date.getMonth(); // 0 = January
  const year = date.getFullYear();
  const d = new Date(date.getFullYear(), month + 1, 0);
  const firstDay = `${year}${months[d.getMonth()]}01`;
  const lastDay = `${year}${months[d.getMonth()]}${d.getDate()}`;

  return {
    firstDay,
    lastDay,
  };
};

export const leaveOnlyNumbers = (number: any) =>
  Number(number?.toString()?.replace(/\D+/g, ''));

export function maskPhone(value: string) {
  let newValue = value.replace(/\D/g, '');

  if (value.length > 10) {
    newValue = value.replace(/^(\d{2})(\d)/g, '($1) $2');
  } else {
    newValue = value.replace(/^(\d{3})(\d)/g, '($1) $2');
  }

  newValue = value.replace(/(\d)(\d{4})$/, '$1-$2');

  return newValue;
}

export const firstLetterUpper = (str: string) =>
  str[0].toUpperCase() + str.substr(1).toLowerCase();

export const convertFileInData64 = (file: File, callbackData64: any) => {
  const reader = new FileReader();
  reader.onload = () => {
    const imageUrl = reader.result;
    callbackData64(imageUrl);
  };
  reader.readAsDataURL(file);
};

export const convertImgInFile = async (
  url: string,
  filename: string,
  callbackFile: Function,
) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type });

    callbackFile(file);
  } catch (error) {
    // console.error(error);
  }
};

// i Thu Feb 24 2022 16:58:22 GMT-0300 (Brasilia Standard Time)
// --->
// { firstDay: "20210201", lastDay: "20210228" }
export const getFirstAndLastDayOfMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const firstDay = `${year}${month.toString().padStart(2, '0')}01`;
  const lastDay = `${year}${month.toString().padStart(2, '0')}${new Date(
    year,
    month,
    0,
  )
    .getDate()
    .toString()
    .padStart(2, '0')}`;
  return { firstDay, lastDay };
};

export const replaceUnderscoresAndCapitalize = (inputString: string) => {
  const modifiedString = inputString.replace(/_/g, ' ').toLowerCase();
  return modifiedString
    .split(' ')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const loadImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', err => reject(err));
    image.src = url;
  });

export const getBase64Image = (src: string, cb: (img: any) => void) => {
  loadImage(src).then((image: any) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(image, 0, 0);
    cb(canvas.toDataURL('image/png'));
  });
};

export const getImgInBase64 = (img: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx?.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
};

export const hexToRgb = (hex?: string, defaultColor?: string) => {
  if (!hex) return defaultColor;
  const hexReplaced = hex.replace('#', '');

  const r = parseInt(hexReplaced.substring(0, 2), 16);
  const g = parseInt(hexReplaced.substring(2, 4), 16);
  const b = parseInt(hexReplaced.substring(4, 6), 16);

  return [r, g, b];
};

export const toBase64 = (text?: string) => {
  if (!text) return undefined;
  const bytes = new TextEncoder().encode(text);
  const binary = Array.from(bytes)
    .map(b => String.fromCharCode(b))
    .join('');
  return window.btoa(binary);
};

export const fromBase64 = (base64?: string) => {
  if (!base64) return undefined;
  const binary = window.atob(base64);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const calculateReadableValue = (valuePerMB?: number) => {
  if (!valuePerMB) return 'R$ 0,00';

  const units = [
    { name: 'KB', exponent: -1 },
    { name: 'MB', exponent: 0 },
    { name: 'GB', exponent: 1 },
    { name: 'TB', exponent: 2 },
  ];

  const converted = units.map(u => ({
    unit: u.name,
    value: valuePerMB * 1024 ** u.exponent,
  }));

  const readable = converted.filter(c => c.value >= 0.01 && c.value < 1000);

  const selected =
    readable.length > 0
      ? readable[0]
      : converted.reduce((a, b) => (a.value < b.value ? a : b));

  const formattedValue = selected.value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${formattedValue} / ${selected.unit}`;
};
